import { useAppForm } from "@/components/form/form-context";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  organisationSettingsQueryOptions,
  updateOrganisationSettingsFn,
  resetOrganisationSettingsFn,
} from "@/server/organisations";
import { GroupedSettingTypeResponse, settingTypesQueryOptions, systemSettingsQueryOptions } from "@/server/settings";
import { SettingListResponse, SettingType } from "@/lib/mma/types.gen";
import { SettingTab } from "./-setting-tab";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { ResponsiveActionBar, ResponsiveFormSection } from "@/components/form/responsive-form";
import { Filter } from "@/routes/_dashboard/settings/-components/settings-toolbar";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { calculateResetValues } from "@/lib/utils";
import { clearGdprFn } from "@/server/gdpr";

export const Route = createFileRoute("/_dashboard/organisations/$orgId/settings")({
  component: RouteComponent,
  loader: async () => {
    return {
      crumb: "Settings",
    };
  },
});

function RouteComponent() {
  const { orgId } = Route.useParams();
  const navigate = useNavigate();
  const organisationSettingsQuery = useQuery(organisationSettingsQueryOptions(Number(orgId)));
  const organisationSettings = organisationSettingsQuery.data?.settings;
  const settingTypesQuery = useQuery(settingTypesQueryOptions);
  const settingTypes = settingTypesQuery.data as GroupedSettingTypeResponse;
  const systemSettingsQuery = useQuery(systemSettingsQueryOptions);
  const systemSettings = systemSettingsQuery.data as SettingListResponse;
  const queryClient = useQueryClient();

  const searchParams = useSearch({ strict: false }) as {
    search?: string;
    tab?: string;
    overwrittenOnly?: string;
  };

  const search = searchParams.search ?? "";
  const activeTab = searchParams.tab ?? "all";
  const overwrittenOnly = searchParams.overwrittenOnly === "true";

  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [saveAffectedItems, setSaveAffectedItems] = useState<string[]>([]);
  const [pendingPayload, setPendingPayload] = useState<Record<string, any> | null>(null);
  const [processedChoiceTypes, setProcessedChoiceTypes] = useState<Set<string>>(new Set());
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [isSearching, setIsSearching] = useState(false);

  // Set default tab in URL if not present
  useEffect(() => {
    if (!searchParams.tab) {
      navigate({
        to: "/organisations/$orgId/settings",
        params: { orgId },
        replace: true,
        search: (prev) => ({
          ...prev,
          tab: "all",
        }),
      });
    }
  }, [searchParams.tab, navigate, orgId]);

  // Debounce the URL search param to avoid filtering on every keystroke
  useEffect(() => {
    setDebouncedSearch(search);
    setIsSearching(false);
  }, [search, overwrittenOnly]);

  const updateOrganisationSettingsMutation = useMutation({
    mutationFn: updateOrganisationSettingsFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organisation-settings", Number(orgId)] });
      setSaveDialogOpen(false);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  const resetOrganisationSettingsMutation = useMutation({
    mutationFn: resetOrganisationSettingsFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organisation-settings", Number(orgId)] });
      setResetDialogOpen(false);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  //Wrapping heavy functions in useRef to avoid re-rendering on tab change since everything rerenders when URL changes.
  const organisationSettingTypesRef = useRef<{
    value: any[];
    dependencies: { settingTypes: any; organisationSettings: any };
  }>({ value: [], dependencies: { settingTypes: null, organisationSettings: null } });

  if (
    organisationSettingTypesRef.current.dependencies.settingTypes !== settingTypes.data ||
    organisationSettingTypesRef.current.dependencies.organisationSettings !== organisationSettings
  ) {
    organisationSettingTypesRef.current = {
      value:
        settingTypes.data
          ?.map((group) => ({
            ...group,
            settings: group.settings
              ?.filter((setting) => setting.domain === "ORGANISATION")
              .map((setting) => {
                return setting;
              }) as unknown as (SettingType & {
              name: string;
            })[],
          }))
          .filter((group) => group.settings?.length > 0)
          .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)) || [],
      dependencies: { settingTypes: settingTypes.data, organisationSettings: organisationSettings },
    };
  }

  const organisationSettingTypes = organisationSettingTypesRef.current.value;

  const defaultSystemValuesRef = useRef<{
    value: Record<string, any>;
    organisationSettingTypes: any[];
    systemSettings: any;
  }>({
    value: {},
    organisationSettingTypes: [],
    systemSettings: null,
  });

  if (
    defaultSystemValuesRef.current.organisationSettingTypes !== organisationSettingTypes ||
    defaultSystemValuesRef.current.systemSettings !== systemSettings
  ) {
    defaultSystemValuesRef.current = {
      value: Object.fromEntries(
        organisationSettingTypes
          .flatMap((group) => group.settings)
          .filter((setting) => Object.hasOwn(systemSettings, setting.name))
          .map((setting) => [setting.name, systemSettings[setting.name]])
      ),
      organisationSettingTypes,
      systemSettings,
    };
  }

  const defaultSystemValues = defaultSystemValuesRef.current.value;

  const overwrittenSettingNames = useMemo(() => {
    if (!organisationSettings) return new Set<string>();
    return new Set(Object.keys(organisationSettings));
  }, [organisationSettings]);

  const filteredSettings = useMemo(() => {
    const lowerSearch = debouncedSearch.toLowerCase();
    return (organisationSettingTypes || [])
      .map((group) => ({
        ...group,
        settings: group.settings?.filter(
          (setting) =>
            (setting.displayName.toLowerCase().includes(lowerSearch) ||
              setting.name.toLowerCase().includes(lowerSearch) ||
              (setting.description?.toLowerCase().includes(lowerSearch) ?? false)) &&
            (!overwrittenOnly || overwrittenSettingNames.has(setting.name))
        ),
      }))
      .filter((group) => (group.settings?.length ?? 0) > 0);
  }, [organisationSettingTypes, debouncedSearch, overwrittenOnly, overwrittenSettingNames]);

  useEffect(() => {
    if (!organisationSettingTypes) return;

    const uniqueChoiceTypes = new Set<string>();

    //Some selects don't come with predefined choices and rely on other data fetched from the server to populate the options.
    //This code takes the name for those choices and matches it with a query key to take the data from that query.
    organisationSettingTypes.forEach((settingGroup: any) => {
      settingGroup.settings.forEach((setting: any) => {
        if (
          setting?.validation?.type === "choices" &&
          typeof setting?.validation?.choices?.choiceType === "string" &&
          setting.validation.choices.choiceType &&
          setting?.validation?.choices?.choiceType !== "predefined"
        ) {
          uniqueChoiceTypes.add(setting.validation.choices.choiceType as string);
        }
      });
    });

    if (uniqueChoiceTypes.size === 0) return;

    // Load choices for each type that hasn't been processed yet.
    uniqueChoiceTypes.forEach((choiceType) => {
      if (!processedChoiceTypes.has(choiceType)) {
        setProcessedChoiceTypes((prev) => new Set(prev).add(choiceType));
      }
    });
  }, [settingTypes]);

  useEffect(() => {
    form.reset(initiateFormValues());
  }, [orgId]);

  const initiateFormValues = () => {
    return {
      ...defaultSystemValues,
      ...organisationSettings,
    } as any;
  };

  const form = useAppForm({
    defaultValues: initiateFormValues(),

    onSubmit: ({ value }) => {
      const defaultValues = initiateFormValues();
      const settingBody: Record<string, any> = {};
      Object.keys(value).forEach((key) => {
        if (value[key] !== defaultValues[key]) {
          settingBody[key] = value[key];
        }
      });

      const changedKeys = Object.keys(settingBody);
      const items: string[] = [];
      organisationSettingTypes.forEach((group) => {
        group.settings?.forEach((setting: any) => {
          if (changedKeys.includes(setting.name)) {
            items.push(setting.displayName);
          }
        });
      });

      setSaveAffectedItems(items);
      setPendingPayload(settingBody);
      setSaveDialogOpen(true);
    },
  });

  const updateOrganisationSettings = async (payload: any) => {
    try {
      const organisationSettingsPromise = updateOrganisationSettingsMutation.mutateAsync({
        data: { orgId: Number(orgId), payload },
      });
      toast.promise(organisationSettingsPromise, {
        loading: "Updating organisation settings...",
        success: () => {
          return "Organisation settings updated";
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const resetToDefault = async () => {
    try {
      const resetOrganisationSettingsPromise = resetOrganisationSettingsMutation.mutateAsync({
        data: { orgId: Number(orgId) },
      });
      toast.promise(resetOrganisationSettingsPromise, {
        loading: "Resetting to default...",
        success: "Settings reset to default",
        error: (error) => error.message || "Failed to reset settings to default. Please try again.",
      });
      await resetOrganisationSettingsPromise;
      form.reset(defaultSystemValues);
    } catch (error) {
      console.error(error);
    }
  };

  const getTabName = (displayName: string) => {
    return displayName.toLowerCase().replace(/ /g, "-");
  };

  // Auto-switch to first available tab when current tab has no filtered results
  useEffect(() => {
    if (filteredSettings.length === 0) return;

    const currentTabName = activeTab;
    const availableTabNames = ["all", ...filteredSettings.map((group) => getTabName(group.displayName ?? ""))];
    const isCurrentTabAvailable = availableTabNames.includes(currentTabName);

    if (!isCurrentTabAvailable && availableTabNames.length > 0) {
      const firstAvailableTab = availableTabNames[0];
      navigate({
        to: "/organisations/$orgId/settings",
        params: { orgId },
        replace: true,
        search: (prev) => ({
          ...prev,
          tab: firstAvailableTab,
        }),
      });
    }
  }, [filteredSettings, activeTab, navigate, orgId]);

  const handleFilterChange = useCallback(
    (filters: { search?: string; tab?: string; overwrittenOnly?: boolean }) => {
      setIsSearching(true);
      navigate({
        to: "/organisations/$orgId/settings",
        params: { orgId },
        replace: true,
        search: (prev) => ({
          ...prev,
          search: filters.search?.trim() || undefined,
          tab: filters.tab || undefined,
          ...(filters.overwrittenOnly !== undefined
            ? { overwrittenOnly: filters.overwrittenOnly ? "true" : undefined }
            : {}),
        }),
      });
    },
    [navigate, orgId]
  );

  const clearGdprMutation = useMutation({
    mutationFn: clearGdprFn,
    onError: (error) => {
      console.error("Failed to clear GDPR:", error);
    },
  });

  const clearGdpr = () => {
    const clearGdprPromise = clearGdprMutation.mutateAsync({
      data: {
        orgId: Number(orgId),
      },
    });
    toast.promise(clearGdprPromise, {
      loading: "Clearing GDPR...",
      success: "GDPR cleared successfully",
      error: (error) => error.message || "Failed to clear GDPR",
    });
  };

  return (
    <>
      <ResponsiveFormSection>
        <div className="mb-4">
          <Filter
            onFilterChange={handleFilterChange}
            defaultSearch={search}
            items={organisationSettingTypes}
            overwrittenOnly={overwrittenOnly}
            showOverwrittenFilter={true}
          />
        </div>
        {isSearching && (
          <div className="absolute top-0 left-0 w-full h-full bg-background/80 z-10 flex justify-center">
            <Badge className="h-10 mt-36" variant="secondary">
              <Spinner />
              Searching...
            </Badge>
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <div className="flex flex-col gap-2 mb-8">
            <Tabs
              value={activeTab}
              onValueChange={(value) => {
                navigate({
                  to: "/organisations/$orgId/settings",
                  params: { orgId },
                  replace: true,
                  search: (prev) => ({
                    ...prev,
                    tab: value,
                  }),
                });
              }}
            >
              {filteredSettings.length > 0 && (
                <TabsList className="w-full flex flex-wrap mb-7">
                  <TabsTrigger value="all" key="all">
                    All
                  </TabsTrigger>
                  {filteredSettings.map((group) => (
                    <TabsTrigger value={getTabName(group.displayName ?? "")} key={group.groupId}>
                      {group.displayName ?? ""}
                    </TabsTrigger>
                  ))}
                </TabsList>
              )}
              {filteredSettings.length > 0 && (
                <TabsContent value="all">
                  <SettingTab
                    searchTerm={debouncedSearch}
                    form={form}
                    settings={filteredSettings.flatMap((group) => group.settings ?? [])}
                    title="All"
                    processedChoiceTypes={processedChoiceTypes}
                  />
                </TabsContent>
              )}
              {filteredSettings.map((settingType) => (
                <TabsContent value={getTabName(settingType.displayName ?? "")} key={settingType.groupId}>
                  <SettingTab
                    clearGdpr={clearGdpr}
                    clearGdprLoading={clearGdprMutation.isPending}
                    searchTerm={debouncedSearch}
                    form={form}
                    settings={settingType.settings ?? []}
                    title={settingType.displayName ?? ""}
                    processedChoiceTypes={processedChoiceTypes}
                  />
                </TabsContent>
              ))}
              {filteredSettings.length === 0 && (
                <Card>
                  <CardContent>
                    <p className="text-md pb-20 pt-15 text-center">
                      {debouncedSearch ? "No results found." : "No overwritten settings found."}
                    </p>
                  </CardContent>
                </Card>
              )}
            </Tabs>
          </div>
          <form.Subscribe selector={(s) => [s.isSubmitting, s.isDirty, s.canSubmit, s.values]}>
            {([isSubmitting, isDirty, canSubmit, values]) => {
              const defaultVals = initiateFormValues();
              const hasChanges = Object.keys(defaultVals).some((key) => (values as any)[key] !== defaultVals[key]);
              return (
                <ResponsiveActionBar>
                  <div className="flex gap-2">
                    <Button type="submit" disabled={isSubmitting || !hasChanges || !canSubmit}>
                      {isSubmitting ? "Saving..." : "Save"}
                    </Button>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          disabled={calculateResetValues(organisationSettings, organisationSettingTypes).length === 0}
                          type="button"
                          variant="outline"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setResetDialogOpen(true);
                          }}
                        >
                          Reset to default
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Reset all settings to their default values.</p>
                      </TooltipContent>
                    </Tooltip>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={isSubmitting || !hasChanges || !canSubmit}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setCancelDialogOpen(true);
                      }}
                    >
                      Reset
                    </Button>
                  </div>
                </ResponsiveActionBar>
              );
            }}
          </form.Subscribe>
        </form>
      </ResponsiveFormSection>

      <ConfirmDialog
        open={resetDialogOpen}
        title="Reset to default"
        description="Are you sure you want to reset all settings to their default values? This action cannot be undone."
        subtitle="The following settings will be reset to their default values:"
        affectedItems={calculateResetValues(organisationSettings, organisationSettingTypes)}
        confirmLabel="Reset"
        cancelLabel="Cancel"
        onConfirm={resetToDefault}
        onCancel={() => {
          setResetDialogOpen(false);
        }}
        loading={resetOrganisationSettingsMutation.isPending}
      />
      <ConfirmDialog
        open={saveDialogOpen}
        title="Save settings"
        description="Are you sure you want to save the following changes?"
        affectedItems={saveAffectedItems}
        confirmLabel="Save"
        cancelLabel="Cancel"
        onConfirm={() => {
          if (pendingPayload) {
            updateOrganisationSettings(pendingPayload);
          }
        }}
        onCancel={() => {
          setSaveDialogOpen(false);
        }}
        confirmVariant="default"
        loadingLabel="Saving..."
        loading={updateOrganisationSettingsMutation.isPending}
      />
      <ConfirmDialog
        open={cancelDialogOpen}
        title="Discard changes"
        description="Are you sure you want to undo all unsaved changes? This will reset the form back to its initial state."
        confirmLabel="Reset"
        cancelLabel="Cancel"
        confirmVariant="destructive"
        onConfirm={() => {
          form.reset(initiateFormValues());
          setCancelDialogOpen(false);
        }}
        onCancel={() => {
          setCancelDialogOpen(false);
        }}
      />
    </>
  );
}
